import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, BarChart, Wrench, FileText, Activity, Search, Building2, Mail, PenLine, Gauge } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const UserMenu = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [userName, setUserName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  
  useEffect(() => {
    const fetchUserName = async () => {
      if (user?.id) {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('name')
            .eq('id', user.id)
            .single();
          
          if (error) {
            console.error('Error fetching user name:', error);
            return;
          }
          
          if (data) {
            setUserName(data.name);
          }
        } catch (error) {
          console.error('Error in user name fetch:', error);
        }
      }
    };
    
    const checkAdminRole = async () => {
      if (user?.id) {
        try {
          const { data, error } = await supabase.rpc('has_role', { _role: 'admin' });
          
          if (error) {
            console.error('Error checking admin role:', error);
            return;
          }
          
          setIsAdmin(!!data);
        } catch (error) {
          console.error('Error checking admin role:', error);
        }
      }
    };
    
    fetchUserName();
    checkAdminRole();
  }, [user]);

  const safeNavigate = (path: string) => {
    try {
      navigate(path);
    } catch (error) {
      console.error('Navigation failed:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      safeNavigate('/');
    } catch (error) {
      toast.error('Error signing out');
    }
  };

  const getUserInitials = () => {
    if (userName) {
      return userName.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar className="h-8 w-8 cursor-pointer">
          <AvatarFallback className="bg-gradient-to-r from-primary to-primary/80 text-white">
            {getUserInitials()}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="flex items-center justify-start p-2">
          <div className="flex flex-col space-y-1 leading-none">
            {userName && (
              <p className="font-medium">{userName}</p>
            )}
            <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            {isAdmin && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary mt-1 w-fit">
                Admin
              </span>
            )}
          </div>
        </div>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuGroup>
          <DropdownMenuLabel className="text-xs text-muted-foreground font-semibold">
            Account
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={() => safeNavigate('/profile')}>
            <User className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => safeNavigate('/settings')}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => safeNavigate('/dashboard')}>
            <Activity className="mr-2 h-4 w-4" />
            <span>My Activity</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs text-muted-foreground font-semibold">
                Admin Tools
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => safeNavigate('/admin/analytics')}>
                <BarChart className="mr-2 h-4 w-4" />
                <span>Analytics</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => safeNavigate('/admin/content')}>
                <FileText className="mr-2 h-4 w-4" />
                <span>Content Dashboard</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => safeNavigate('/admin/search-indexing')}>
                <Search className="mr-2 h-4 w-4" />
                <span>Search Indexing</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => safeNavigate('/admin/institutional')}>
                <Building2 className="mr-2 h-4 w-4" />
                <span>Institutional</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => safeNavigate('/admin/emails')}>
                <Mail className="mr-2 h-4 w-4" />
                <span>Email</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => safeNavigate('/admin/legal')}>
                <FileText className="mr-2 h-4 w-4" />
                <span>Rechtsdokumente</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => safeNavigate('/write')}>
                <PenLine className="mr-2 h-4 w-4" />
                <span>Writer (beta)</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => safeNavigate('/admin/writer-telemetry')}>
                <Gauge className="mr-2 h-4 w-4" />
                <span>Writer Telemetry</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs text-muted-foreground font-semibold">
                Development Tools
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => safeNavigate('/dev/storage-diagnostics')}>
                <Wrench className="mr-2 h-4 w-4" />
                <span>Storage Diagnostics</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => safeNavigate('/dev/storage-documentation')}>
                <Wrench className="mr-2 h-4 w-4" />
                <span>Storage Documentation</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
